//DatePicker is a compat component - its internal architecture does not follow all the principles regular Fluent UI v9 components follow, and it is nit stable, so using fluent 8 date picker for now.
import { Calendar, DatePicker, ICalendar, ICalendarProps, IDatePicker, LayerHost, getId } from "@fluentui/react";
import { Field } from "@fluentui/react-components";
import classNames from "classnames";
import dayjs from "dayjs";
import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { setStartOfWeek } from "../reducers/diningReducer";
import { cafeSelector, stationSelector, weekStartSelector } from "../selectors/diningSelector";

import styles from "../styles/content.module.css";
import GettingStarted from "./GettingStarted";
import { DayOfWeekSelector } from "./MenuItemsGridList/DayOfWeekSelector";
import { FiltrableMenuItemBank } from "./MenuItemsGridList/FilterableMenuItemBank";
import { MenuMessageBar } from "./MenuPreview/MenuMessageBar";
import MenuPreview from "./MenuPreview/MenuPreview";
import StationSelector from "./StationSelector/StationSelector";
import { UpdateStation } from "./UpdateStation";

const FocusedCalendar = (props: ICalendarProps) => {
  const { componentRef, ...calendarProps } = props;
  const calendarRef = useRef<ICalendar>(null);

  useImperativeHandle(componentRef, () => ({
    focus: () => {
      window.requestAnimationFrame(() => calendarRef.current?.focus());
    },
  }));

  return <Calendar {...calendarProps} componentRef={calendarRef} />;
};

export default function MenuManagement() {
  const dispatch = useDispatch();

  const selectedCafe = useSelector(cafeSelector);
  const selectedStation = useSelector(stationSelector);
  const weekStart = useSelector(weekStartSelector);

  const calendarRef = useRef<IDatePicker>(null);
  const layerHostId = getId("layerHost");
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(() => {
    const parsedWeekStart = dayjs(weekStart);
    return parsedWeekStart.isValid() ? parsedWeekStart.toDate() : undefined;
  });

  useEffect(() => {
    const parsedWeekStart = dayjs(weekStart);

    setSelectedDate((currentDate) => {
      if (!parsedWeekStart.isValid()) {
        return undefined;
      }

      if (currentDate && dayjs(currentDate).startOf("week").isSame(parsedWeekStart, "day")) {
        return currentDate;
      }

      return parsedWeekStart.toDate();
    });
  }, [weekStart]);

  return (
    <div className={classNames(styles.mainContainer)}>
      {/* Left Sidebar */}
      <div className={styles.leftPanel}>
        <StationSelector />
      </div>

      {/* Main Content */}
      {!selectedCafe ? (
        <GettingStarted />
      ) : (
        <>
          <div className={styles.horizontalContentStack}>
            {/* Item bank / controls */}
            <div className={styles.itemBankSection}>
              <Field label="Menu for the week of:">
                <DatePicker
                  className={styles.datePickerStyle}
                  minDate={dayjs().startOf("week").toDate()}
                  value={selectedDate}
                  initialPickerDate={selectedDate ?? new Date()}
                  showGoToToday
                  placeholder="Select a date..."
                  ariaLabel="Select a date"
                  showMonthPickerAsOverlay
                  firstWeekOfYear={1}
                  allowTextInput
                  disableAutoFocus
                  openOnClick
                  calendarAs={FocusedCalendar}
                  componentRef={calendarRef}
                  onSelectDate={(date) => {
                    if (date) {
                      setSelectedDate(date);
                      dispatch(setStartOfWeek(dayjs(date).startOf("week").toDate()));
                    }
                  }}
                />
              </Field>

              <FiltrableMenuItemBank />
              <DayOfWeekSelector />
              {selectedStation && <UpdateStation />}
            </div>

            {/* Menu preview */}
            <div className={styles.menuPreviewSection}>
              <MenuMessageBar />
              <MenuPreview />
            </div>
          </div>
        </>
      )}
      {/* Layer host for Fluent v8 overlays */}
      <LayerHost id={layerHostId} />
    </div>
  );
}
